<template lang="pug">
.backup-details(v-if='backup')
  hr.mb-4
  .d-flex.align-items-center.justify-content-between.mb-4
    a.d-inline-flex.align-items-center.text-decoration-none(
      @click='goBack'
      style='cursor: pointer'
    )
      icon.icon--blue.me-2(name='chevron-left')
      h1.h4.mb-0.font-color-blue {{ $t('pages.backupDetails.backup_details') }}
    span.d-inline-flex.align-items-center.fw-bold(
      :style='{ color: statusConfig.color }'
      v-if='statusConfig'
    )
      icon.me-1(:name='statusConfig.icon')
      | {{ statusConfig.text }}

  hr.mb-4

  .row
    .col-12
      .card
        .card-body
          .d-flex.mb-2
            strong.me-1 {{ $t('pages.backupDetails.created_at') }}
            span {{ backup.created_at | dateTime }} {{ backup.created_at | time }}
          .d-flex.mb-2
            strong.me-1 {{ $t('pages.backupDetails.expire_at') }}
            span {{ backup.expire_at | dateTime }} {{ backup.expire_at | time }}
          .d-flex.mb-2
            strong.me-1 {{ $t('pages.backupDetails.period') }}
            span {{ backup.metadata.date_start | dateTime }} {{ $t('backupCardTable.period_to') }} {{ backup.metadata.date_end | dateTime }}
          .d-flex.align-items-center.mb-2
            strong.me-1 {{ $t('pages.backupDetails.includeBackoundSheets') }}
            icon.ms-1(
              :name='getBoolIcon(backup.metadata.include_background_sheet)'
              :style='{ color: getBoolColor(backup.metadata.include_background_sheet) }'
            )
          .d-flex.align-items-center
            strong.me-1 {{ $t('pages.backupDetails.includeStorageDocuments') }}
            icon.ms-1(
              :name='getBoolIcon(backup.metadata.include_storage_documents)'
              :style='{ color: getBoolColor(backup.metadata.include_storage_documents) }'
            )

  .row.mt-3(v-if='isExpired')
    .col-12
      .alert.alert-simple-warning.d-flex.align-items-center
        icon.me-2(name='triangle-exclamation')
        span {{ $t('pages.backupDetails.expiredWarning') }}

  .row.mt-3
    .col-12
      .card(style='background-color: #f6f8f8')
        .card-body
          .d-flex.align-items-center.justify-content-between(
            :aria-controls='"collapse-files"'
            :aria-expanded='filesExpanded ? "true" : "false"'
            :data-bs-target='"#collapse-files"'
            data-bs-toggle='collapse'
            style='cursor: pointer'
          )
            .fw-bold {{ $t('pages.backupDetails.filesBackup') }}
            icon.icon--blue(name='chevron-down')
          #collapse-files.collapse(:class='{ show: filesExpanded }')
            .mt-3
              .d-flex.flex-column.flex-md-row.align-items-md-center.justify-content-between.mb-2.gap-2(
                :key='file.id'
                v-for='file in files'
              )
                .d-flex.align-items-center
                  .d-flex.align-items-center.justify-content-center.me-3(
                    style='width: 44px; height: 44px; border-radius: 50%; background-color: #e3eef3'
                  )
                    icon(name='download' style='color: #013d52')
                  div
                    .fw-bold {{ file.filename }}
                    .text-muted {{ file.filesize }}
                button.btn.d-flex.align-items-center.w-100.w-md-auto-btn(
                  @click='downloadFile(file, "document")'
                  style='background-color: #013d52; color: #fff'
                  type='button'
                  v-if='!isExpired'
                )
                  icon.me-2(name='download')
                  | {{ $t('pages.backupDetails.downloadFile') }}

  .row.mt-3(v-if='storageFiles.length')
    .col-12
      .card(style='background-color: #f6f8f8')
        .card-body
          .d-flex.align-items-center.justify-content-between(
            :aria-controls='"collapse-storage-files"'
            :aria-expanded='storageFilesExpanded ? "true" : "false"'
            :data-bs-target='"#collapse-storage-files"'
            data-bs-toggle='collapse'
            style='cursor: pointer'
          )
            .fw-bold {{ $t('pages.backupDetails.files_storage') }}
            icon.icon--blue(name='chevron-down')
          #collapse-storage-files.collapse(
            :class='{ show: storageFilesExpanded }'
          )
            .mt-3
              .d-flex.flex-column.flex-md-row.align-items-md-center.justify-content-between.mb-2.gap-2(
                :key='file.id'
                v-for='file in storageFiles'
              )
                .d-flex.align-items-center
                  .d-flex.align-items-center.justify-content-center.me-3(
                    style='width: 44px; height: 44px; border-radius: 50%; background-color: #e3eef3'
                  )
                    icon(name='download' style='color: #013d52')
                  div
                    .fw-bold {{ file.filename }}
                    .text-muted {{ file.filesize }}
                button.btn.d-flex.align-items-center.w-100.w-md-auto-btn(
                  @click='downloadFile(file, "storage")'
                  style='background-color: #013d52; color: #fff'
                  type='button'
                  v-if='!isExpired'
                )
                  icon.me-2(name='download')
                  | {{ $t('pages.backupDetails.downloadFile') }}
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
import BackupService from '@/modules/account/services/http/BackupService';
import BackupState from '@/enums/BackupState';
export default {
  name: 'DetailsBackup',
  components: {
    Icon,
  },
  data() {
    return {
      backup: null,
      files: [],
      storageFiles: [],
    };
  },
  computed: {
    isExpired() {
      return this.backup.status === 'expired';
    },
    filesExpanded() {
      return this.storageFiles.length === 0;
    },
    storageFilesExpanded() {
      return this.storageFiles.length === 0;
    },
    statusConfig() {
      const styles = {
        completed: { color: '#28a745', icon: 'circle-check' },
        expired: { color: '#dc3545', icon: 'clock' },
      };
      const style = styles[this.backup.status];
      if (!style) return null;
      return {
        ...style,
        text: BackupState.getText(this.backup.status, this.$i18n),
      };
    },
  },
  async created() {
    await this.loadBackup();
    this.fetchDocuments();
    this.$store.dispatch('page/setTitle', this.$t('pages.backupDetails.title'));
    this.$store.dispatch('page/setHead', this.$t('pages.backupDetails.title'));
  },
  methods: {
    async loadBackup() {
      const stored = localStorage.getItem('selectedBackup');

      if (stored) {
        this.backup = JSON.parse(stored);
        return;
      }

      const uuid = this.$route.params.uuid;
      const result = await BackupService.show(uuid);
      this.backup = result.data;
    },
    getBoolIcon(value) {
      return value ? 'check' : 'xmark';
    },
    getBoolColor(value) {
      return value ? '#28a745' : '#dc3545';
    },
    goBack() {
      localStorage.removeItem('selectedBackup');
      this.$router.push({ name: 'account.backup' });
    },
    async fetchDocuments() {
      this.$loading(true);
      try {
        const uuid = this.$route.params.uuid;
        const requests = [BackupService.listDocuments(uuid)];

        if (this.backup.metadata.include_storage_documents) {
          requests.push(BackupService.listItems(uuid));
        }

        const [documentsResult, storageResult] = await Promise.all(requests);

        this.files = documentsResult.data;
        this.storageFiles = storageResult?.data ?? [];
      } finally {
        this.$loading(false);
      }
    },
    async downloadFile(file, type) {
      this.$loading(true);
      try {
        const uuid = this.$route.params.uuid;
        const downloadFn =
          type === 'storage'
            ? BackupService.downloadStorageItem
            : BackupService.downloadDocument;

        const response = await downloadFn(uuid, file.id);
        window.open(response.data.url, '_blank');
      } finally {
        this.$loading(false);
      }
    },
  },
};
</script>

<style scoped lang="stylus">
.w-md-auto-btn
  @media (min-width: 768px)
    width: auto !important

[data-bs-toggle='collapse'] .icon
  transition: transform 0.2s
[data-bs-toggle='collapse'][aria-expanded='true'] .icon
  transform: rotate(180deg)
</style>
